import React, { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"

import {
  Col,
  Row,
  Input,
  Label,
  FormGroup,
  Spinner,
  Alert,
  Badge,
} from "reactstrap"
import NumberFormat from "react-number-format"
import classNames from "classnames"
import { AiOutlineClose, AiOutlineInfoCircle } from "react-icons/ai"
import DatePicker from "react-datepicker"
import {
  validarCuit,
  getDatosTitularAlta,
  getEvaluacionDatosCliente,
} from "api"

import {
  TITULAR_LEFT,
  TITULAR_RIGHT,
  MESES,
  TIPOS_SOCIEDAD,
  detectarTipoSociedadDesdeCuit,
  getReglasTipoSociedad,
} from "./Titular.constants"
import toastHandler from "toastHandler"
import refsStore from "refsStore"
import { filterFields } from "helpers/titular_helpers"
import { Skeleton } from "@mui/material"
import { parse, isValid, format } from "date-fns"
import CustomReactSelect from "components/CustomReactSelect/CustomReactSelect"

// ============================================
// Componente de alerta para tipo de sociedad
// ============================================
const TipoSociedadAlert = ({ deteccion, tipoSeleccionado }) => {
  if (!deteccion) return null

  const reglas = tipoSeleccionado
    ? getReglasTipoSociedad(tipoSeleccionado)
    : null

  return (
    <div className="mb-3">
      {/* Mensaje de detección */}
      {deteccion.mensaje && (
        <Alert
          color={
            deteccion.confianza === "ALTA"
              ? "success"
              : deteccion.confianza === "BAJA"
                ? "warning"
                : "info"
          }
          className="py-2 mb-2"
        >
          <div className="d-flex align-items-center">
            <AiOutlineInfoCircle className="me-2" size={18} />
            <small>{deteccion.mensaje}</small>
          </div>
        </Alert>
      )}

      {/* Info de personas requeridas según tipo */}
      {reglas && tipoSeleccionado && tipoSeleccionado !== "PF" && (
        <div className="bg-light rounded p-2 mt-2">
          <small className="text-muted d-block mb-1">
            <strong>Órgano de administración:</strong>{" "}
            {reglas.descripcionOrgano}
          </small>
          <small className="text-muted d-block">
            <strong>Personas requeridas para firma:</strong>{" "}
            {reglas.personasRequeridas.join(", ")}
          </small>
          {reglas.notaEspecial && (
            <small className="text-warning d-block mt-1">
              <AiOutlineInfoCircle className="me-1" />
              {reglas.notaEspecial}
            </small>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// Componente InputForm (con soporte para tipoSociedad)
// ============================================
const InputForm = ({
  item,
  props,
  pathname,
  isOnSolicitud,
  showCuitCloseBtn = true,
  deteccionTipoSociedad,
  onTipoSociedadChange,
}) => {
  const [notFoundMessage, setNotFoundMessage] = useState("")
  const [isLoadingSearch, setIsLoadingSearch] = useState(false)

  const setError = toastHandler(state => state.setError)
  const setActividad = refsStore(state => state.setActividad)

  const parseDescripcion = arr => {
    return arr.map(obj => {
      const { codigo, descripcion } = obj
      return { ...obj, descripcion: `${codigo} - ${descripcion}` }
    })
  }

  const {
    formik,
    provincias,
    actividad,
    personeria,
    referidos: referidoNombre,
    condicionIva,
    isCuitFounded,
    setIsCuitFounded,
    setDatosDB,
    asesorComercial,
    actividadEspecifica,
    situacionBCRA,
    tamanioEmpresa,
    tipoCanalComercializacion,
    sectorEconomico,
    originador,
    ciudades,
    isLoadingCiudades,
    esEvaluador,
  } = props

  const {
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
    handleReset,
    setValues,
    setFieldValue,
    setFieldTouched,
  } = formik

  const {
    value,
    refValue,
    label,
    type,
    isSearchable,
    readOnly,
    disableRef,
    optionValueToShow,
    helpText,
  } = item

  // Agregar tiposSociedad a las opciones
  const options = {
    provincia: provincias,
    actividad,
    personeria,
    referidoNombre,
    condicionIva,
    asesorComercial,
    referido: [
      { codigo: true, descripcion: "Sí" },
      { codigo: false, descripcion: "No" },
    ],
    mesCierreBalance: MESES,
    actividadEspecifica,
    situacionBCRAID: parseDescripcion(situacionBCRA),
    tamanioEmpresaId: parseDescripcion(tamanioEmpresa),
    tipoCanalComercializacionID: parseDescripcion(tipoCanalComercializacion),
    sectorEconomico: parseDescripcion(sectorEconomico),
    originador,
    ciudades,
    // NUEVO: Tipos de sociedad
    tiposSociedad: TIPOS_SOCIEDAD,
  }

  const selectedValues = {
    actividadCodigo: "codigo",
    actividad: "descripcion",
    asesorComercial: "descripcion",
    provincia: "codigo",
    deudorPrevisional: "codigo",
    referido: "codigo",
    referidoNombre: "descripcion",
    personeria: "codigo",
    condicionIva: "codigo",
    mesCierreBalance: "descripcion",
    actividadEspecifica: "descripcion",
    situacionBCRAID: "codigo",
    tamanioEmpresaId: "codigo",
    tipoCanalComercializacionID: "codigo",
    sectorEconomico: "codigo",
    originador: "descripcion",
    ciudadID: "codigo",
    // NUEVO: Tipo de sociedad
    tipoSociedad: "codigo",
  }

  const loadingRefStates = {
    ciudades: isLoadingCiudades,
  }
  const isLoadingInput = loadingRefStates[refValue]

  const handleSubmitCuit = e => {
    if (e.key === "Backspace" || e.key === "Delete") {
      return
    }
    const cuitValue = e.target.value
    if (cuitValue.length === 11) {
      e.key = "Enter"
    }
    if (e.key === "Enter" && cuitValue !== "") {
      setIsLoadingSearch(true)
      validarCuit(cuitValue)
        .then(isValid => {
          if (isValid) {
            getDatosTitularAlta(cuitValue, esEvaluador)
              .then(data => {
                if (data !== "" && data) {
                  const newValues = { ...data }
                  newValues.composicionSocietaria =
                    newValues.composicionSocietaria.map(item => ({
                      ...item,
                      id: 0,
                    }))

                  // NUEVO: Detectar tipo de sociedad del CUIT
                  const deteccion = detectarTipoSociedadDesdeCuit(cuitValue)
                  if (deteccion.tipoDetectado) {
                    newValues.tipoSociedad = deteccion.tipoDetectado
                  } else if (deteccion.sugerencia && !newValues.tipoSociedad) {
                    // Si hay sugerencia y no viene tipo del backend, usar la sugerencia
                    newValues.tipoSociedad = deteccion.sugerencia
                  }

                  setValues({ ...values, titular: newValues })

                  const actividadCodigo = data.actividadCodigo
                  const currentActividad = data.actividad

                  if (
                    currentActividad &&
                    actividadCodigo &&
                    !actividad.some(item => item.codigo == actividadCodigo)
                  ) {
                    const parsedActividad = {
                      codigo: actividadCodigo,
                      descripcion: currentActividad,
                    }
                    setActividad([...actividad, parsedActividad])
                  }

                  getEvaluacionDatosCliente(cuitValue)
                    .then(dataInDB => {
                      setDatosDB(dataInDB)
                    })
                    .catch()
                  setIsCuitFounded(true)
                }
              })
              .catch(e => {
                const statusCode = e.status
                if (statusCode === 409) {
                  const message = e.data.message
                  setError({ message })
                } else {
                  setError(e)
                }
              })
              .finally(() => setIsLoadingSearch(false))
          } else {
            setNotFoundMessage("El CUIT ingresado no es válido")
            setIsLoadingSearch(false)
          }
        })
        .catch(e => {
          const statusCode = e.status
          if (statusCode === 409) {
            const message = e.data.message
            setError({ message })
          } else {
            setError(e)
          }
        })
        .finally(() => setIsLoadingSearch(false))
    }
  }

  const handleCleanSearch = () => {
    handleReset()
    setIsCuitFounded(false)
    setNotFoundMessage("")
  }

  const handleChangeCuit = e => {
    if (e.target.value.length !== 0 && e.target.value === "") {
      return
    }

    setNotFoundMessage("")

    const newEvent = e
    const { value } = newEvent.target

    const regexOnlyNumbers = /^[0-9]{0,20}$/g
    const regexOnlyNumbersAndDots = /^[0-9.]{0,20}$/g

    if (value.includes(".") || value.includes("-")) {
      const valueWithouCharacters = value.replace(/[^0-9]/g, "").trim()
      if (regexOnlyNumbersAndDots) {
        newEvent.target.value = valueWithouCharacters
        handleChange(newEvent)
      }
    }
    if (regexOnlyNumbers.test(value)) {
      handleChange(newEvent)
    }
    if (e.target.value.length === 11) {
      handleSubmitCuit(e)
    }
  }

  const resolvedOptions = options[refValue] || options[value]
  const selectedValue = selectedValues[value]
  const formValue = values?.titular !== undefined ? values?.titular[value] : ""

  const isValidOption = resolvedOptions?.some(
    item => item[selectedValue] == formValue,
  )

  const reactSelectValue = resolvedOptions?.find(
    item => item[selectedValue] == formValue,
  )
  const formattedReactSelectValue = {
    value: reactSelectValue?.[selectedValue],
    label: optionValueToShow
      ? reactSelectValue?.[optionValueToShow]
      : reactSelectValue?.descripcion,
  }

  const updateInformationOrigin = name => {
    setFieldValue(`titular.informacionObtenidaDetalle.${name}`, "EWAN")
  }

  const selectHandleChange = e => {
    const { value: selectValue, name } = e.target

    if (name === "titular.actividadCodigo") {
      const actividadDescripcion = options.actividad.find(
        act => act.codigo == selectValue,
      )?.descripcion
      setFieldValue("titular.actividad", actividadDescripcion)
      updateInformationOrigin("titular.actividad")
    }

    // NUEVO: Notificar cambio de tipo de sociedad
    if (name === "titular.tipoSociedad" && onTipoSociedadChange) {
      onTipoSociedadChange(selectValue)
    }

    if (name === "titular.provincia") {
      // Limpiar ciudad si cambia provincia
    }

    updateInformationOrigin(name)
    handleChange(e)
  }

  const textHandleChange = e => {
    const { name } = e.target
    updateInformationOrigin(name)
    handleChange(e)
  }

  const dateHandleChange = date => {
    updateInformationOrigin(value)
    formik.setFieldValue(value, dateToString(date))
  }

  const stringToDate = dateString => parse(dateString, "dd/MM/yyyy", new Date())
  const dateToString = date => (date ? format(date, "dd/MM/yyyy") : null)

  const types = {
    text: isSearchable ? (
      <>
        <div className="d-flex gap-2 align-items-center">
          <Input
            type="text"
            name={`titular.${value}`}
            value={formValue}
            onChange={handleChangeCuit}
            placeholder={`Ingrese ${label.toLowerCase()}..`}
            bsSize="sm"
            onBlur={handleBlur}
            invalid={touched[value] && errors[value]}
            disabled={isLoadingSearch || isCuitFounded || isOnSolicitud}
            onKeyDown={handleSubmitCuit}
            autoComplete="on"
          />
          {isCuitFounded && showCuitCloseBtn && (
            <button
              className="bg-transparent m-0 p-0 border-0"
              onClick={handleCleanSearch}
            >
              <AiOutlineClose />
            </button>
          )}
          {isLoadingSearch && <Spinner color="primary" size={"sm"} />}
        </div>
        {notFoundMessage && (
          <small className="text-danger position-absolute top-2 m-0 p-0">
            {notFoundMessage}
          </small>
        )}
      </>
    ) : readOnly ? (
      <div>{formValue || "-"}</div>
    ) : (
      <Input
        type="text"
        name={`titular.${value}`}
        value={formValue}
        onChange={textHandleChange}
        placeholder={`Ingrese ${label.toLowerCase()}..`}
        bsSize="sm"
        onBlur={handleBlur}
        invalid={touched[value] && errors[value]}
        disabled={pathname !== "/alta-titular" && value == "cuit"}
        autoComplete="off"
      />
    ),
    date: (
      <DatePicker
        name={`titular.${value}`}
        locale="es"
        dateFormat="dd/MM/yyyy"
        maxDate={new Date()}
        selected={
          isValid(stringToDate(formValue)) ? stringToDate(formValue) : null
        }
        onChange={dateHandleChange}
        className={classNames("form-control form-control-sm", {
          "is-invalid": touched[value] && errors[value],
        })}
        placeholderText="DD/MM/AAAA"
        autoComplete="off"
        showYearDropdown
        showMonthDropdown
        customInput={<NumberFormat autoComplete="off" format="##/##/####" />}
      />
    ),
    select: isLoadingInput ? (
      <Skeleton height={35} />
    ) : (
      <>
        <CustomReactSelect
          options={resolvedOptions?.map(item => ({
            value: item.codigo,
            label: optionValueToShow
              ? item[optionValueToShow]
              : item.descripcion,
          }))}
          value={isValidOption ? formattedReactSelectValue : ""}
          className={classNames({
            "react-select-error": errors[value] && touched[value],
          })}
          name={`titular.${value}`}
          noOptionsMessage={() => "No hay coincidencias"}
          isDisabled={disableRef ? (!values?.titular ?? [disableRef]) : false}
          onBlur={() => setFieldTouched(value)}
          onChange={(option, props) => {
            const event = {
              target: {
                name: props.name,
                value:
                  selectedValue === "codigo" ? option?.value : option?.label,
              },
            }
            selectHandleChange(event)
          }}
        />
        {/* Help text para tipo de sociedad */}
        {helpText && (
          <small className="text-muted d-block mt-1">
            <AiOutlineInfoCircle className="me-1" />
            {helpText}
          </small>
        )}
      </>
    ),
    number: (
      <Input
        type="number"
        name={`titular.${value}`}
        value={formValue}
        onChange={textHandleChange}
        placeholder={`Ingrese ${label.toLowerCase()}..`}
        bsSize="sm"
        onBlur={handleBlur}
        invalid={touched[value] && errors[value]}
        disabled={pathname !== "/alta-titular" && value == "cuit"}
      />
    ),
  }

  const informacionObtenidaDetalleValue =
    values?.titular?.informacionObtenidaDetalle?.[value]

  return (
    <FormGroup
      row
      style={{ maxWidth: 600 }}
      className="d-flex align-items-center"
    >
      <Label lg={6} className="text-nowrap fw-bold">
        {label}{" "}
        {informacionObtenidaDetalleValue && (
          <span className="text-muted fw-normal fs-12">
            ({informacionObtenidaDetalleValue})
          </span>
        )}
      </Label>
      <Col lg={12} xl={6}>
        {types[type]}
      </Col>
    </FormGroup>
  )
}

// ============================================
// Componente principal Titular
// ============================================
const Titular = ({ props, isOnSolicitud, showCuitCloseBtn }) => {
  const { pathname } = useLocation()
  const { isCuitFounded, formik } = props
  const formValues = formik.values

  // Estado para la detección de tipo de sociedad
  const [deteccionTipoSociedad, setDeteccionTipoSociedad] = useState(null)

  // Detectar tipo cuando cambia el CUIT
  useEffect(() => {
    const cuit = formValues.titular?.cuit
    if (cuit && cuit.length >= 11) {
      const deteccion = detectarTipoSociedadDesdeCuit(cuit)
      setDeteccionTipoSociedad(deteccion)
    } else {
      setDeteccionTipoSociedad(null)
    }
  }, [formValues.titular?.cuit])

  // Handler para cambio de tipo de sociedad
  const handleTipoSociedadChange = nuevoTipo => {
    console.log("Tipo de sociedad cambiado a:", nuevoTipo)
    // Aquí podrías disparar lógica adicional, como actualizar
    // los campos requeridos de personas relacionadas
  }

  if (isCuitFounded || isOnSolicitud || showCuitCloseBtn) {
    return (
      <div>
        {/* Alerta de tipo de sociedad */}
        {isCuitFounded && deteccionTipoSociedad && (
          <TipoSociedadAlert
            deteccion={deteccionTipoSociedad}
            tipoSeleccionado={formValues.titular?.tipoSociedad}
          />
        )}

        <Row>
          <Col xs={12} md={6}>
            {TITULAR_LEFT.map((item, key) => {
              if (
                item.isDynamic &&
                (!formValues.titular?.referido ||
                  formValues.titular?.referido === "false")
              )
                return null
              return (
                <InputForm
                  key={key}
                  {...{
                    props,
                    item,
                    pathname,
                    isOnSolicitud,
                    showCuitCloseBtn,
                    deteccionTipoSociedad,
                    onTipoSociedadChange: handleTipoSociedadChange,
                  }}
                />
              )
            })}
          </Col>
          <Col xs={12} md={6}>
            {TITULAR_RIGHT.filter(item => filterFields(item, formValues)).map(
              (item, key) => {
                return (
                  <InputForm
                    key={key}
                    {...{
                      props,
                      item,
                      pathname,
                      deteccionTipoSociedad,
                      onTipoSociedadChange: handleTipoSociedadChange,
                    }}
                  />
                )
              },
            )}
          </Col>
        </Row>

        {formValues.titular?.informacionObtenidaDe && (
          <div className="mt-4 text-secondary">
            Información obtenida de {formValues.titular?.informacionObtenidaDe}
          </div>
        )}
      </div>
    )
  }

  return (
    <Row>
      <Col xs={12} md={6}>
        <InputForm {...{ props, item: TITULAR_LEFT[0], pathname }} />
      </Col>
    </Row>
  )
}

export default Titular
